import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Form, Button } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { Tooltip } from "@mui/material";
import { fetchDataRead } from "../../Utility";
import AppDataGrid from "../../components/common/AppDataGrid";
import axios from "axios";

function AddLeaveEntry() {
    const userData = sessionStorage.getItem('user');
    const userObj = userData ? JSON.parse(userData) : {};
    const [editId, setEditId] = useState(null);
    const routeLocation = useLocation();
    const [balance, setBalance] = useState([]);
    const [users, setUsers] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [managers, setManagers] = useState([]);

    const navigate = useNavigate();

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const isAdmin = userObj?.role_name === "Admin";

    const [form, setForm] = useState({
        leave_entry_id: "",
        userid: isAdmin ? "" : userObj.user_id,
        leave_id: "",
        apply_date: "",
        from_date: "",
        to_date: "",
        reason: "",
        approved_by: "",
        approved_date: "",
        status: "P",
        school_id: "",
        user_name: "",
        leave_description: "",
        approved_by_name: "",
        school_name: "",
        total_days: "",
        rejected_reason: "",
        max_days_to_avail: "",
    });

    // NEW: school cutoff info
    const [schoolTime, setSchoolTime] = useState({
        start_time: null,     // "HH:MM"
        cut_off_hours: null,  // integer hours
    });
    const [isHolidayMonthBlocked, setIsHolidayMonthBlocked] = useState(false);

    const fetchusers = async () => {
        try {
            const response = await axios.post(baseUrl + "/employees/", {
                action: "READ",
                school_id: userObj.school_id,
            });
            setUsers(response.data || []);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const fetchleaves = async () => {
        try {
            const response = await axios.post(baseUrl + "/LeaveMasterRead/", {
                action: "READ",
                school_id: userObj.school_id,
            });
            setLeaves(response.data || []);
            sessionStorage.setItem("leaves", JSON.stringify(response.data || []));
        } catch (error) {
            console.error("Error fetching leaves:", error);
        }
    };

    // NEW: fetch school time window for cutoff
    const fetchSchoolTime = async () => {
        try {
            const resp = await axios.get(baseUrl + "/schooltime/", {
                params: { school_id: userObj.school_id },
            });
            const row = Array.isArray(resp.data) ? resp.data[0] : resp.data;
            setSchoolTime({
                start_time: row?.start_time ?? null,
                cut_off_hours: row?.cut_off_hours ?? row?.cut_off_time ?? null,
            });
        } catch (e) {
            console.warn("School time not available; cutoff disabled.", e?.response?.data || e.message);
            setSchoolTime({ start_time: null, cut_off_hours: null });
        }
    };

    const LeaveBalance = async (userId) => {
        try {
            if (!userId) return setBalance([]);
            const response = await axios.get(baseUrl + "/leaveBalance", {
                params: {
                    _userid: userId,
                    _year_id: userObj.academic_year_id,
                    _school_id: userObj.school_id,
                },
            });

            if (Array.isArray(response.data)) {
                setBalance(response.data);
            } else {
                setBalance([]);
            }
        } catch (error) {
            console.error("Error fetching leaves:", error);
            setBalance([]);
        }
    };

    useEffect(() => {
        fetchusers();
        fetchleaves();
        fetchDataRead("/holiday/", setHolidays, userObj.school_id)
        fetchDataRead("/employees/", setManagers, userObj.school_id);
        fetchSchoolTime();
    }, []);

    useEffect(() => {
        if (isAdmin && form.userid) {
            LeaveBalance(form.userid);
        } else if (!isAdmin) {
            LeaveBalance(userObj.user_id);
        }
    }, [form.userid]);

    const pad2 = (n) => String(n).padStart(2, "0");
    const toDayKey = (input) => {
        if (!input) return "";
        const s = String(input).trim();
        const core = s.includes(" ") ? s.split(" ")[0] : s.split("T")[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(core)) return core;
        const d = new Date(s.replace(" ", "T"));
        return isNaN(d) ? "" : `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    };

    const normalizeYesNo = (v) => {
        const s = String(v ?? "Yes").trim().toLowerCase();
        if (["yes", "y", "true", "1"].includes(s)) return "Yes";
        if (["no", "n", "false", "0"].includes(s)) return "No";
        return "Yes";
    };

    // NEW: helpers for cutoff logic
    const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const todayStr = ymd(new Date());
    const tomorrowStr = (() => { const t = new Date(); t.setDate(t.getDate() + 1); return ymd(t); })();
    const parseTimeToHM = (tstr) => {
        if (!tstr) return { hh: 0, mm: 0 };
        const parts = String(tstr).split(":").map(x => parseInt(x, 10));
        return { hh: parts[0] || 0, mm: parts[1] || 0 };
    };
    const hasTodayCutoffPassedRaw = () => {
        const start = schoolTime.start_time;
        const cutoffHrs = schoolTime.cut_off_hours;
        if (!start || cutoffHrs == null) return false;
        const now = new Date();
        const base = new Date();
        const { hh, mm } = parseTimeToHM(start);
        const cutoff = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm, 0, 0);
        cutoff.setHours(cutoff.getHours() + Number(cutoffHrs || 0));
        return now > cutoff;
    };
    
    const cutoffActive = hasTodayCutoffPassedRaw() && !isAdmin;

    const holidayKeys = useMemo(() => {
        if (!Array.isArray(holidays)) return [];
        const keys = holidays.map(h =>
            typeof h === "string" ? toDayKey(h) : toDayKey(h?.holiday_date ?? h?.date)
        ).filter(Boolean);
        return Array.from(new Set(keys)).sort();
    }, [holidays]);

    const holidaySet = useMemo(() => new Set(holidayKeys), [holidayKeys]);

    const balanceColumns = [
        {
            field: "leave_name",
            headerName: "Leave Type",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "opening_balance",
            headerName: "Opening Balance",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "curr_balance",
            headerName: "Current Balance",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "used_leaves",
            headerName: "Used Leaves",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
    ];

    useEffect(() => {
        if (routeLocation.state?.leaveData) {
            const leaveData = routeLocation.state.leaveData;
            setForm({
                ...leaveData,
                half_day_type: leaveData.half_day_type || "",
                total_days: leaveData.from_date && leaveData.to_date
                    ? (new Date(leaveData.to_date) - new Date(leaveData.from_date)) / (1000 * 60 * 60 * 24) + 1
                    : ""
            });
            setEditId(leaveData.leave_entry_id);
            window.history.replaceState({}, document.title);
        }
    }, [routeLocation]);

    useEffect(() => {
        if (!form.from_date || !form.to_date) {
            setForm((prev) => ({ ...prev, total_days: "" }));
            return;
        }

        function calculateDays(startDate, endDate, holidaySet, includeSunAndHolidays) {
            if (!startDate || !endDate) return 0;
            let count = 0;
            const formatDate = (d) => {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
            };

            let dt = new Date(startDate);
            const end = new Date(endDate);

            while (dt <= end) {
                const currentDate = formatDate(dt);
                const isSunday = dt.getDay() === 0;
                const isHoliday = holidaySet.has(currentDate);

                if (includeSunAndHolidays === "No") {
                    if (isSunday || isHoliday) {
                        dt.setDate(dt.getDate() + 1);
                        continue;
                    }
                }
                count++;
                dt.setDate(dt.getDate() + 1);
            }
            return count;
        }

        const includeSunAndHolidays = form.consider_holidays || "Yes";
        let days = calculateDays(form.from_date, form.to_date, holidaySet, includeSunAndHolidays);

        if (days === 1 && form.half_day_type) {
            days = 0.5;
        }

        setForm((prev) => ({ ...prev, total_days: days }));
    }, [
        form.from_date,
        form.to_date,
        form.leave_id,
        form.half_day_type,
        form.consider_holidays,
        holidays,
        leaves,
        holidaySet
    ]);

    const fromDateMin = cutoffActive ? tomorrowStr : todayStr;
    const toDateMin = form.from_date ? form.from_date : fromDateMin;

    useEffect(() => {
        if (!cutoffActive) return;
        setForm(prev => {
            const next = { ...prev };
            let changed = false;
            if (prev.from_date === todayStr) { next.from_date = ""; changed = true; }
            if (prev.to_date === todayStr) { next.to_date = ""; changed = true; }
            return changed ? next : prev;
        });
    }, [cutoffActive]);

    useEffect(() => {
        if (!form.leave_id || !form.from_date || holidays.length === 0 || leaves.length === 0) {
            setIsHolidayMonthBlocked(false);
            return;
        }

        const selectedLeave = leaves.find(l => String(l.leave_id) === String(form.leave_id));
        const restrictHolidays = (selectedLeave?.restrict_holidays || "").toLowerCase() === "yes";
        const isLOP = (selectedLeave?.leave_description || selectedLeave?.leave_name || "")
            .toLowerCase()
            .includes("loss of pay");

        if (isLOP) {
            setIsHolidayMonthBlocked(false);
            return;
        }

        if (!restrictHolidays) {
            setIsHolidayMonthBlocked(false);
            return;
        }

        const leaveMonth = new Date(form.from_date).getMonth();
        const leaveYear = new Date(form.from_date).getFullYear();

        const holidayInSameMonth = holidays.some(h => {
            const hDateStr = typeof h === "string" ? h : (h.holiday_date || h.date);
            if (!hDateStr) return false;
            const hDate = new Date(hDateStr);
            return hDate.getMonth() === leaveMonth && hDate.getFullYear() === leaveYear;
        });

        if (holidayInSameMonth) {
            setIsHolidayMonthBlocked(true);
            toast.error("No leave can be applied in the month of public holidays (except Loss of Pay).");
        } else {
            setIsHolidayMonthBlocked(false);
        }
    }, [form.leave_id, form.from_date, holidays, leaves]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm((prev) => {
            if ((id === "from_date" || id === "to_date") && cutoffActive && value === todayStr) {
                toast.error("Today's leave cannot be applied after the school's cutoff time.");
                return prev;
            }

            let updatedForm = { ...prev, [id]: value };
            if (id === "from_date") {
                const selectedDate = new Date(value);
                const day = selectedDate.getDay();
                const lv = leaves.find((x) => String(x.leave_id) === String(prev.leave_id));
                const restrictWeekends = (lv?.restrict_weekends || "").toLowerCase() === "yes";
                const leaveDesc = lv?.leave_description || "Leave";

                if (restrictWeekends && day === 6) {
                    const sun = new Date(selectedDate);
                    const mon = new Date(selectedDate);
                    sun.setDate(sun.getDate() + 1);
                    mon.setDate(mon.getDate() + 2);

                    const formatted = (d) => d.toISOString().split("T")[0];
                    const satStr = formatted(selectedDate);
                    const sunStr = formatted(sun);
                    const monStr = formatted(mon);

                    const totalDays = 3;
                    const message =
                        `You have applied for ${leaveDesc} on ${satStr} (Saturday).\n` +
                        `As per company policy (Restrict Weekends = Yes):\n\n` +
                        `• ${leaveDesc}: ${satStr} (Sat)\n` +
                        `• Loss of Pay: ${sunStr} (Sun)\n` +
                        `• Loss of Pay: ${monStr} (Mon)\n\n` +
                        `Total Days: ${totalDays}\n\nDo you want to continue?`;

                    if (window.confirm(message)) {
                        updatedForm = { ...prev, from_date: satStr, to_date: monStr, total_days: totalDays };
                    } else {
                        return prev;
                    }
                } else if (restrictWeekends && day === 1) {
                    const sat = new Date(selectedDate);
                    const sun = new Date(selectedDate);
                    sat.setDate(sat.getDate() - 2);
                    sun.setDate(sun.getDate() - 1);

                    const formatted = (d) => d.toISOString().split("T")[0];
                    const satStr = formatted(sat);
                    const sunStr = formatted(sun);
                    const monStr = formatted(selectedDate);

                    const totalDays = 3;
                    const message =
                        `You have applied for ${leaveDesc} on ${monStr} (Monday).\n` +
                        `As per company policy (Restrict Weekends = Yes):\n\n` +
                        `• Loss of Pay: ${satStr} (Sat)\n` +
                        `• Loss of Pay: ${sunStr} (Sun)\n` +
                        `• ${leaveDesc}: ${monStr} (Mon)\n\n` +
                        `Total Days: ${totalDays}\n\nDo you want to continue?`;

                    if (window.confirm(message)) {
                        updatedForm = { ...prev, from_date: satStr, to_date: monStr, total_days: totalDays };
                    } else {
                        return prev;
                    }
                } else {
                    updatedForm = { ...prev, [id]: value };
                }
            }

            if (id === "leave_id") {
                const lv = leaves.find((x) => String(x.leave_id) === String(value));
                updatedForm.consider_holidays = normalizeYesNo(lv?.consider_holidays);
                updatedForm.consider_half_day = normalizeYesNo(lv?.consider_half_day);
            }

            const selectedLeave = leaves.find(
                (leave) => leave.leave_id.toString() === (id === "leave_id" ? value : prev.leave_id)
            );
            if (selectedLeave?.leave_description?.toLowerCase() === "sick leave") {
                const maxDaysAllowed = parseInt(selectedLeave.max_days_to_avail, 10);
                const defaultFrom = (cutoffActive ? tomorrowStr : todayStr);
                let fromDate = new Date(id === "from_date" ? value : prev.from_date);
                let toDate = new Date(id === "to_date" ? value : prev.to_date);

                updatedForm.from_date = defaultFrom;
                fromDate = new Date(defaultFrom);

                if (fromDate && toDate && !isNaN(maxDaysAllowed)) {
                    const diffInDays = (toDate - fromDate) / (1000 * 60 * 60 * 24) + 1;
                    if (diffInDays > maxDaysAllowed) {
                        alert(`Sick Leave cannot exceed ${maxDaysAllowed} continuous days.`);
                        const allowedToDate = new Date(fromDate);
                        allowedToDate.setDate(fromDate.getDate() + maxDaysAllowed - 1);
                        updatedForm.to_date = allowedToDate.toISOString().split("T")[0];
                    }
                } else if (toDate < fromDate) {
                    updatedForm.to_date = defaultFrom;
                }
            }

            return updatedForm;
        });
    };

    const norm = (s) => (s || "").trim().toLowerCase();

    const leaveBalanceById = useMemo(() => {
        const map = new Map();
        balance.forEach(b => {
            if (b.leave_id != null) map.set(String(b.leave_id), Number(b.curr_balance ?? 0));
        });

        if (map.size === 0) {
            leaves.forEach(lv => {
                const hit = balance.find(b => norm(b.leave_name) === norm(lv.leave_description));
                map.set(String(lv.leave_id), Number(hit?.curr_balance ?? 0));
            });
        }
        return map;
    }, [balance, leaves]);

    // FIXED: Check if selected leave is LOP
    const isLOPLeave = useMemo(() => {
        const selectedLeave = leaves.find(l => String(l.leave_id) === String(form.leave_id));
        return (selectedLeave?.leave_description || "").toLowerCase().includes("loss of pay");
    }, [form.leave_id, leaves]);

    const checkMonthlyLeaveAlreadyApplied = async (userId, leaveId, fromDate) => {
        if (!userId || !leaveId || !fromDate) return false;

        try {
            const resp = await axios.get(`${baseUrl}/main/leavehistory`, {
                params: {
                    emp_id: userId,
                    academic_year_id: userObj.academic_year_id,
                    school_id: userObj.school_id,
                },
            });

            const leaveHistory = resp.data?.[0]?.leaves_history?.history || [];
            const selectedLeave = leaves.find(lv => String(lv.leave_id) === String(leaveId));
            const leaveDesc = selectedLeave?.leave_description?.toLowerCase() || '';

            // Only restrict General Leave
            if (!leaveDesc.includes('general')) return false;

            const fromDateObj = new Date(fromDate);
            const month = fromDateObj.getMonth();
            const year = fromDateObj.getFullYear();

            const existingGeneralLeave = leaveHistory.find((lh) => {
                const lhDesc = (lh.leave_description || '').toLowerCase();
                if (!lhDesc.includes('general')) return false;

                const [day, monStr] = lh.from_date.split('-').map(s => s.trim());
                const monMap = {
                    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
                    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
                };
                const leaveMonth = monMap[monStr.toLowerCase()] ?? -1;
                const leaveYear = lh.year || fromDateObj.getFullYear();

                return leaveMonth === month && 
                       leaveYear === year && 
                       lh.status?.toLowerCase() !== 'rejected';
            });

            return !!existingGeneralLeave;

        } catch (error) {
            console.error('Error fetching leave history:', error);
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basic validations
        if (!form.leave_id || form.leave_id === "0") {
            toast.error("Please select a leave type");
            return;
        }
        if (!form.from_date) {
            toast.error("Please select From Date");
            return;
        }
        if (!form.to_date) {
            toast.error("Please select To Date");
            return;
        }

        // Monthly General Leave restriction check (skip for LOP)
        if (!isLOPLeave) {
            const userId = isAdmin ? form.userid : userObj.user_id;
            const alreadyApplied = await checkMonthlyLeaveAlreadyApplied(
                userId,
                form.leave_id,
                form.from_date
            );

            if (alreadyApplied) {
                toast.error("General Leave is already applied in this month with Pending or Approved status. Only 1 General Leave per month is allowed.");
                return;
            }
        }

        // Cutoff validation
        if ((form.from_date === todayStr || form.to_date === todayStr) && cutoffActive) {
            toast.error("Today's leave cannot be applied after the school's cutoff time.");
            return;
        }

        // Holiday validation
        const from = new Date(form.from_date);
        const to = new Date(form.to_date);
        let appliedOnHolidayOnly = true;

        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
            const dayStr = d.toISOString().split("T")[0];
            const isHoliday = Array.isArray(holidays) &&
                holidays.some(h =>
                    typeof h === "string"
                        ? h.split("T")[0].split(" ")[0] === dayStr
                        : (h?.holiday_date ?? h?.date ?? "").split("T")[0].split(" ")[0] === dayStr
                );

            if (!isHoliday) {
                appliedOnHolidayOnly = false;
            }
        }

        if (appliedOnHolidayOnly) {
            toast.error("Leave cannot be applied on a holiday");
            return;
        }

        // Total days validation
        if (!form.total_days || form.total_days <= 0) {
            toast.error("Total Days must be greater than 0");
            return;
        }

        // Balance validation - SKIP FOR LOP
        if (!isLOPLeave) {
            const balForChosen = leaveBalanceById.get(String(form.leave_id)) ?? 0;
            if (balForChosen <= 0) {
                toast.error("No balance available for the selected leave type.");
                return;
            }
        }

        // Submit logic
        const today = new Date().toISOString().split("T")[0];
        const formData = {
            userid: isAdmin ? form.userid : userObj.user_id,
            leave_id: form.leave_id || 0,
            apply_date: today,
            from_date: form.from_date || "",
            to_date: form.to_date || "",
            reason: form.reason || "",
            half_day_type: form.half_day_type || null,
            total_days: form.half_day_type ? 1 : form.total_days,
            school_id: userObj.school_id,
            academic_year_id: userObj.academic_year_id,
            action: editId !== null ? "UPDATE" : "CREATE",
        };

        if (editId !== null) {
            formData.leave_entry_id = editId;
        }

        try {
            const response = await axios.post(baseUrl + "/LeaveEntry/", formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            toast.success(editId ? "Record Updated Successfully" : "Record Added Successfully");
            
            // Reset form after successful submission
            setForm({
                userid: isAdmin ? "" : userObj.user_id,
                leave_id: "",
                leave_description: "",
                apply_date: "",
                from_date: "",
                to_date: "",
                reason: "",
                total_days: "",
                consider_holidays: "",
                school_id: "",
            });
            setEditId(null);

        } catch (error) {
            console.error("Error submitting data:", error.response?.data || error.message);
            if (error.response?.data?.error?.includes("already exists")) {
                toast.error("Record already exists");
            } else if (error.response?.data?.error?.includes("Leave dates overlap with an existing leave for this user")) {
                toast.error("Leave dates overlap with an existing leave for this user");
            } else if (error.response?.data?.error?.includes("From date cannot be after To date.")) {
                toast.error("From date cannot be after To date.");
            } else if (error.response?.data?.error?.includes("You cannot apply for more days than your available leave balance.")) {
                toast.error("You cannot apply for more days than your available leave balance.");
            } else if (error.response?.data?.error?.includes("You cannot take leave immediately after a day without a gap.")) {
                toast.error("You cannot take leave immediately after a day without a gap.");
            } else if (error.response?.data?.error?.includes("You cannot take leave immediately before a day without a gap.")) {
                toast.error("You cannot take leave immediately before a day without a gap.");
            } else if (error.response?.data?.error?.includes("Max carryforward cannot be greater than yearly accumulation")) {
                toast.error("Max carryforward cannot be greater than yearly accumulation");
            } else {
                toast.error("Unexpected error occurred");
            }
        }
    };

    const selectedLeave = leaves.find(
        (leave) => leave.leave_id.toString() === form.leave_id.toString()
    );

    return (
        <div className='pageMain'>
            <div className='pageRight'>
                <div style={{ padding: "20px" }}>
                    <h4>Leave Balance</h4>
                    <div className="commonTable height100">
                        <div className="tableBody">
                            <AppDataGrid
                                rows={
                                    Array.isArray(balance) && balance.length > 0
                                        ? balance
                                        : [{ leave_name: "No Records Found" }]
                                }
                                columns={balanceColumns}
                                getRowId={(row) => row.leave_name || Math.random()}
                                showToolbar={false}
                                pagination
                                pageSizeOptions={[10, 20, 50]}
                                density="compact"
                                maxHeight={300}
                            />
                        </div>
                    </div>
                </div>

                <div className='pageBody'>
                    <ToastContainer />
                    <Container fluid>
                        <div className=''>
                            <Card>
                                <Card.Body>
                                    <form className="" onSubmit={handleSubmit}>
                                        <div className=''>
                                            <Row>
                                                <Col xs={12}>
                                                    <h6 className='commonSectionTitle'>Apply Leave</h6>
                                                </Col>
                                            </Row>
                                            <Row className="g-3 align-items-end">
                                                {isAdmin && (
                                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                                        <Form.Group className="commonInput">
                                                            <Form.Label>Employee</Form.Label>
                                                            <Form.Check
                                                                type="checkbox"
                                                                label="Apply leave for myself"
                                                                checked={form.userid === userObj.user_id}
                                                                onChange={(e) => {
                                                                    setForm((prev) => ({
                                                                        ...prev,
                                                                        userid: e.target.checked ? userObj.user_id : "",
                                                                    }));
                                                                }}
                                                                className="mb-2"
                                                            />
                                                            {form.userid !== userObj.user_id && (
                                                                <Form.Select id="userid" value={form.userid} onChange={handleInputChange}>
                                                                    <option value="" disabled hidden>Select Employee</option>
                                                                    {(managers || []).map((emp) => (
                                                                        <option key={emp.emp_id} value={emp.emp_id}>
                                                                            {emp.emp_name}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            )}
                                                        </Form.Group>
                                                    </Col>
                                                )}

                                                <Col xs={12} md={6} lg={4} xxl={3}>
                                                    <Form.Group className="commonInput">
                                                        <Form.Label>Leave Type <span className="requiredStar">*</span></Form.Label>
                                                        <Form.Select id="leave_id" value={form.leave_id} onChange={handleInputChange}>
                                                            <option value="" disabled hidden>Select Leave Type</option>
                                                            {leaves.map((lv) => (
                                                                <option key={lv.leave_id} value={lv.leave_id}>
                                                                    {lv.leave_description}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>

                                                <Col xs={12} md={6} lg={4} xxl={3}>
                                                    <Form.Group className="commonInput">
                                                        <Form.Label>From Date<span className="requiredStar">*</span></Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            id="from_date"
                                                            value={form.from_date}
                                                            onChange={handleInputChange}
                                                            min={(cutoffActive ? tomorrowStr : todayStr)}
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col xs={12} md={6} lg={4} xxl={3}>
                                                    <Form.Group className="commonInput">
                                                        <Form.Label>To Date<span className="requiredStar">*</span></Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            id="to_date"
                                                            value={form.to_date}
                                                            onChange={handleInputChange}
                                                            min={form.from_date || (cutoffActive ? tomorrowStr : todayStr)}
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col xs={12} md={6} lg={4} xxl={3}>
                                                    <Form.Group className="commonInput">
                                                        <Form.Label>Total Days<span className="requiredStar">*</span></Form.Label>
                                                        <Form.Control readOnly id="total_days" value={form.total_days} />
                                                    </Form.Group>
                                                </Col>

                                                <Col xs={8} md={8} lg={8} xxl={6}>
                                                    <Form.Group className="commonInput">
                                                        <Form.Label>Reason</Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            id="reason"
                                                            value={form.reason}
                                                            placeholder="Enter Reason"
                                                            onChange={handleInputChange}
                                                            rows={4}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                        <div className="d-flex justify-content-between mt-3">
                                            <div>
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    className="btn-info clearBtn"
                                                    onClick={() => setForm({
                                                        apply_date: "",
                                                        from_date: "",
                                                        to_date: "",
                                                        total_days: "",
                                                        leave_id: "",
                                                        userid: isAdmin ? "" : userObj.user_id,
                                                        reason: "",
                                                    })}
                                                >
                                                    Reset
                                                </Button>
                                            </div>
                                            <div>
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    className="btn-danger secondaryBtn me-2"
                                                    onClick={() => window.history.back()}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    className="btn-success primaryBtn"
                                                    disabled={isHolidayMonthBlocked}
                                                >
                                                    Submit
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </Card.Body>
                            </Card>
                        </div>
                    </Container>
                </div>
            </div>
        </div>
    );
}

export default AddLeaveEntry;